import React, { useLayoutEffect, useRef } from "react"
import { Box } from "@mui/material"
import { MRT_ColumnDef, MaterialReactTable } from "material-react-table"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import { tableTypes } from "../../../pages/PnlReporting/constants"
import { setReportingScrollTop } from "../../../redux/slices/pnlReportingSlice"
import { defaultTableProps } from "../ResourceBudgetTable/constants"

const scrollToRowByIndex = (
  container: HTMLElement,
  data: {}[],
  lineItem: string,
) => {
  const rowIndex = data.findIndex(
    (row: any) => String(row?.line_item) === lineItem,
  )
  if (rowIndex < 0) return false

  const rows = container.querySelectorAll("tbody tr")
  const row = rows[rowIndex] as HTMLElement | undefined
  if (!row) return false

  const header = container.querySelector("thead")
  const headerHeight = header?.getBoundingClientRect().height ?? 0
  const containerTop = container.getBoundingClientRect().top + headerHeight
  const rowTop = row.getBoundingClientRect().top
  container.scrollTop += rowTop - containerTop
  return true
}

type tableProps = {
  table: string
  columns: MRT_ColumnDef<any>[]
  data: {}[]
  pinningCols: string[]
  highlightedLineItem?: string | null
}

export const _PNLTable = (params: tableProps) => {
  const dispatch = useAppDispatch()
  const { loading } = useAppSelector((state) => state.app)
  const reportingScrollTop = useAppSelector(
    (state) => state.pnlReporting.reportingScrollTop,
  )
  const selectedClassId = useAppSelector(
    (state) => state.pnlReporting.pnlReportingSelectedClass?.class_id,
  )
  const { columns, data, pinningCols, table, highlightedLineItem } = params
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const scrollTopRef = useRef(0)
  const isReportingTable = table === tableTypes.REPORTING

  useLayoutEffect(() => {
    return () => {
      if (isReportingTable) {
        dispatch(setReportingScrollTop(scrollTopRef.current))
      }
    }
  }, [isReportingTable, dispatch])

  useLayoutEffect(() => {
    if (!isReportingTable) return
    if (reportingScrollTop || highlightedLineItem) return
    const container = scrollContainerRef.current
    if (container) {
      container.scrollTop = 0
      scrollTopRef.current = 0
    }
  }, [isReportingTable, selectedClassId, reportingScrollTop, highlightedLineItem])

  useLayoutEffect(() => {
    if (!isReportingTable) return
    if (!reportingScrollTop && !highlightedLineItem) return

    const lineItem = highlightedLineItem
      ? String(highlightedLineItem)
      : null
    let cancelled = false

    const tryRestore = () => {
      if (cancelled || loading) return false
      const container = scrollContainerRef.current
      if (!container) return false

      if (reportingScrollTop > 0) {
        container.scrollTop = reportingScrollTop
        return true
      }

      if (lineItem && data.length) {
        return scrollToRowByIndex(container, data, lineItem)
      }

      return false
    }

    let attempts = 0
    const run = () => {
      if (tryRestore() || cancelled || attempts++ >= 20) return
      requestAnimationFrame(run)
    }
    run()
    const timeoutId = window.setTimeout(() => tryRestore(), 50)

    return () => {
      cancelled = true
      window.clearTimeout(timeoutId)
    }
  }, [
    isReportingTable,
    reportingScrollTop,
    highlightedLineItem,
    data,
    loading,
  ])

  const tableElement = (
    <MaterialReactTable
      key={`pnl-${table}-table`}
      columns={columns}
      data={data}
      {...defaultTableProps}
      muiTableContainerProps={{
        sx: isReportingTable
          ? {
              overflow: "visible",
              maxHeight: "none",
            }
          : {
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              position: "absolute",
              overflow: "auto",
            },
      }}
      enableEditing={false}
      initialState={{
        density: "compact",
      }}
      muiTableBodyRowProps={({ row }) => {
        const lineItem = row.original?.line_item
        const lineItemStr =
          lineItem === undefined || lineItem === null
            ? undefined
            : String(lineItem)
        return {
          hover: false,
          sx: highlightedLineItem &&
            lineItemStr === String(highlightedLineItem)
            ? { backgroundColor: "#E8F0FE" }
            : undefined,
        }
      }}
      state={{
        isLoading: loading,
        columnPinning: {
          left: pinningCols,
        },
      }}
    />
  )

  if (!isReportingTable) {
    return tableElement
  }

  return (
    <Box
      data-pnl-reporting-scroll=""
      ref={scrollContainerRef}
      onScroll={(event) => {
        scrollTopRef.current = event.currentTarget.scrollTop
      }}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "auto",
      }}
    >
      {tableElement}
    </Box>
  )
}
